import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plane, FolderOpen, CalendarDays, FileSpreadsheet, UserCog, LogOut, ChevronRight, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { enablePush } from "@/components/app/NotificationBell";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PREFS = [["shifts", "Upcoming shifts"], ["attendance", "Missing clock-ins & forgotten clock-outs"], ["leave", "Leave approvals & declines"], ["announcements", "Staff announcements"],
  ["meetings", "Meeting reminders"], ["corrections", "Timesheet corrections"], ["documents", "Certificate & document expiry"]];

export const NotificationPrefs = () => {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get("/me/notification-prefs").then((r) => setP(r.data)).catch(() => setP({}));
  }, []);
  const toggle = async (k, v) => {
    const next = { ...p, [k]: v };
    setP(next);
    try { await api.put("/me/notification-prefs", next); } catch (e) { toast.error(formatError(e)); }
  };
  const perm = typeof Notification === "undefined" ? "unsupported" : Notification.permission;
  return (
    <div className="rounded-2xl border border-[#E9EEF2] bg-white p-4" data-testid="notification-prefs">
      <p className="font-semibold text-[#0B2D5B]">Phone notifications</p>
      <p className="mt-1 text-sm text-slate-500">Choose what we alert you about. Everything still shows in the bell inside Clockaroo.</p>
      {perm !== "granted" && <Button className="mt-3 h-11 w-full rounded-full bg-[#00C16B] font-semibold hover:bg-[#00A85C]" onClick={enablePush} data-testid="prefs-enable-push-btn"><BellRing className="mr-2 h-4 w-4" />Enable notifications on this phone</Button>}
      {p && PREFS.map(([k, label]) => (
        <label key={k} className="flex items-center justify-between gap-3 border-t border-[#E9EEF2] py-3 text-sm first-of-type:mt-3">
          <span className="min-w-0">{label}</span><Switch checked={p[k] !== false} onCheckedChange={(v) => toggle(k, v)} data-testid={`pref-${k}`} />
        </label>
      ))}
    </div>
  );
};

const LinkRow = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} className="flex min-h-14 items-center gap-3 border-t border-[#E9EEF2] px-4 py-3 first:border-t-0 active:bg-[#E9EEF2]" data-testid={testid}>
    <Icon className="h-5 w-5 shrink-0 text-[#00A85C]" /><span className="flex-1 font-medium">{label}</span><ChevronRight className="h-4 w-4 text-slate-400" />
  </Link>
);

export default function ProfilePage() {
  const { user, membership, logout } = useAuth();
  const navigate = useNavigate();
  const full = membership.app_access !== "leave_only";
  return (
    <div className="mx-auto w-full max-w-xl space-y-4" data-testid="profile-page">
      <div className="flex items-center gap-3 rounded-2xl bg-[#0B2D5B] p-5 text-white">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#00C16B] font-bold">{user.name?.split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
        <div className="min-w-0"><p className="break-words font-display text-lg font-bold" data-testid="profile-name">{user.name}</p><p className="break-all text-sm text-slate-200">{user.email}</p><p className="text-xs capitalize text-slate-300">{membership.role} · {membership.business_name}</p></div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-[#E9EEF2] bg-white">
        <LinkRow to="/app/leave" icon={Plane} label="Leave requests" testid="profile-link-leave" />
        {full && <LinkRow to="/app/rosters" icon={CalendarDays} label="My roster & availability" testid="profile-link-rosters" />}
        {full && <LinkRow to="/app/timesheets" icon={FileSpreadsheet} label="My hours & timesheets" testid="profile-link-timesheets" />}
        <LinkRow to="/app/documents" icon={FolderOpen} label="Documents & certificates" testid="profile-link-documents" />
        <LinkRow to="/app/account-settings" icon={UserCog} label="Account & security" testid="profile-link-account" />
      </div>
      <NotificationPrefs />
      <Button variant="outline" className="h-12 w-full rounded-full text-red-600" onClick={async () => { navigate("/login", { replace: true }); await logout(); }} data-testid="profile-logout-btn"><LogOut className="mr-2 h-4 w-4" />Log out</Button>
    </div>
  );
}
